import { ImageSourceSizes, Typography, getTypographyDefaultColor } from '@ag1/ui';
import clsx from 'clsx';
import { Dispatch, SetStateAction } from 'react';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { TestimonialsV5 } from 'types/studio/studio';
import CustomPortableText from '../../PortableText/PortableText';
import clickableStyles from './clickableStyles.module.scss';
import styles from './styles.module.scss';

type TestimonialsCardProps = {
  setOpened?: Dispatch<SetStateAction<boolean>>;
  setSelected?: Dispatch<SetStateAction<Record<string, unknown>>>;
  clickable?: boolean;
  card: Pick<NonNullable<TestimonialsV5['cards']>[number], 'image' | 'title' | 'subtitle' | 'text'>;
  makeSquare?: boolean;
  imageSizes?: ImageSourceSizes;
  visibleOnPageLoad: boolean;
};

const TestimonialsCard = ({
  title,
  subtitle,
  titlePosition,
  image,
  text,
  textAlignment,
  card,
  cards,
  theme,
  clickable,
  makeSquare,
  imageSizes,
  setOpened,
  setSelected,
  visibleOnPageLoad
}: NonNullable<TestimonialsV5['cards']>[number] &
  TestimonialsCardProps &
  Pick<TestimonialsV5, 'textAlignment' | 'cards' | 'theme'>) => {
  const hasDesktopSlider = cards && cards.length > 3;

  const handleCardClick = () => {
    if (!setOpened || !setSelected) {
      return;
    }
    setOpened(true);
    setSelected(card);
  };

  return clickable ? (
    <button type="button" onClick={handleCardClick} className={clickableStyles.cardButton}>
      <article className={clickableStyles.root}>
        {image && (
          <div className={clickableStyles.imageCol}>
            <StudioBaseImageV2
              image={image}
              className={clickableStyles.image}
              sizes={imageSizes}
              eagerLoading={visibleOnPageLoad}
            />
          </div>
        )}
        <div
          className={clsx(
            clickableStyles.contentCol,
            textAlignment && textAlignment.alignLeft && clickableStyles.desktopLeft,
            textAlignment && textAlignment.alignMobileLeft && clickableStyles.mobileLeft
          )}>
          {title && (
            <Typography
              component="h3"
              variant="subtitle"
              size="1"
              weight="400"
              noPadding
              className={clsx(
                clickableStyles.title,
                titlePosition === 'under' && clickableStyles.small
              )}
              color={getTypographyDefaultColor(theme)}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              component="p"
              variant="body"
              size="1"
              noPadding
              className={clsx(
                clickableStyles.subtitle,
                titlePosition === 'above' && clickableStyles.colorGrey,
                getThemeCSSClass(theme)
              )}>
              {subtitle}
            </Typography>
          )}
        </div>
      </article>
    </button>
  ) : (
    <article className={clsx(styles.root, hasDesktopSlider && styles.sliderDesktopActive)}>
      {image && (
        <div className={clsx(styles.imageCol, hasDesktopSlider && styles.sliderDesktopActive)}>
          <StudioBaseImageV2
            image={image}
            className={clsx(
              styles.image,
              hasDesktopSlider && styles.sliderDesktopActive,
              makeSquare && styles.square
            )}
            sizes={imageSizes}
            eagerLoading={visibleOnPageLoad}
          />
        </div>
      )}
      <div
        className={clsx(
          styles.contentCol,
          textAlignment && textAlignment.alignLeft && styles.desktopLeft,
          textAlignment && textAlignment.alignMobileLeft && styles.mobileLeft,
          cards?.length === 2 && styles.desktopLeft,
          hasDesktopSlider && styles.sliderDesktopActive
        )}>
        {title && (
          <Typography
            component="h3"
            variant="subtitle"
            size="1"
            weight="700"
            noPadding
            className={clsx(styles.title, titlePosition === 'under' && styles.small)}
            color={getTypographyDefaultColor(theme)}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body"
            size="1"
            noPadding
            className={clsx(
              styles.subtitle,
              titlePosition === 'above' && styles.colorGrey,
              getThemeCSSClass(theme)
            )}>
            {subtitle}
          </Typography>
        )}
        {text && (
          <Typography
            component="div"
            variant="body"
            size="1"
            weight="400"
            noPadding
            className={clsx(styles.text, titlePosition === 'under' && styles.order)}
            color={getTypographyDefaultColor(theme)}>
            <CustomPortableText value={text} />
          </Typography>
        )}
      </div>
    </article>
  );
};

export default TestimonialsCard;
